import { camelizeKeys } from "humps";
import { merge, pick } from "lodash";
import AppContext from "PFApp/app_context";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { DEFAULT_DATE_FORMAT } from "PFCore/helpers/date";
import usePageWrapper from "PFCore/helpers/use_page_wrapper";
import { useCustomization } from "PFCore/hooks/queries/accounts/use_customization";
import { Customization, LocalesData } from "PFTypes";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { EditAccountPage } from "./edit_account_page/edit_account_page";
import { useAccountFormData } from "./use_account_form_data";
import { useSaveAccount } from "./use_save_account";

const EditAccountController = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { t, i18n } = useTranslation(["admin", "translation", "saasAdmin"]);
  const {
    store: {
      currentAccount: { customization }
    }
  } = useContext(AppContext);
  const adminLocale = camelizeKeys<Customization>(customization);

  const { data: accountData, isLoading: isAccountLoading } = useAccountFormData(Number(accountId));
  const { saveAccount, isSaving, formErrors } = useSaveAccount(Number(accountId));
  const { data: localesData, isLoading: isLocalesDataLoading } = useCustomization(Number(accountId), {
    enabled: !!accountId
  });

  const parsedLocalesData = useMemo<LocalesData>(() => {
    const localeFields = [
      "availableCurrencies",
      "currency",
      "availableLocales",
      "allowedLocales",
      "locale",
      "availableDateFormats",
      "dateFormatKey",
      "twelveHourClock"
    ] as const;

    if (accountId) {
      return pick(localesData, localeFields) as LocalesData;
    }

    const { availableLocales, availableDateFormats } = adminLocale;

    const defaultLocale = (availableLocales ?? []).find((locale) => locale === i18n.language);
    const dateFormatKey = "day_month";

    return merge(
      {
        availableLocales: [],
        dateFormatKey
      },
      pick(adminLocale, localeFields) as LocalesData,
      {
        locale: defaultLocale ? i18n.language : "",
        displayDateFormat: availableDateFormats?.[dateFormatKey]?.display_format || DEFAULT_DATE_FORMAT
      }
    );
  }, [accountId, adminLocale, i18n.language, localesData]);

  const WrappedEditAccountPage = usePageWrapper(EditAccountPage);

  return (
    <WrappedEditAccountPage
      loading={isAccountLoading || (!!accountId && isLocalesDataLoading)}
      saving={isSaving}
      errors={
        (!accountData && !isAccountLoading) || (!parsedLocalesData && !isLocalesDataLoading)
          ? ["There was an error loading the account form."]
          : []
      }
      errorComponent={
        <NotFoundComponent
          message={t("saasAdmin:accounts.account.notFoundMessage")}
          buttonMessage={t("translation:comeBackLater")}
          buttonUrl="/saas_admin/accounts"
        />
      }
      account={accountData}
      saveAccount={saveAccount}
      formErrors={formErrors}
      accountId={Number(accountId)}
      localesData={parsedLocalesData}
    />
  );
};

export default EditAccountController;
